import { Skeleton, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { customFieldTypeMap } from '../components/CustomField/CustomField.type';
import EditOrganizationForm from '../components/Forms/EditOrganizationForm';
import useOrganizationInfo from '../hooks/useOrganizationInfo';
import { DetailedUser, ORGANIZATION_FORM_FIELDS_KEY, Organization } from '../model/types';
import { useCurrentUserStore } from '../store/RootStoreContext';
import { isAdminOrAgent } from '../utils/accountHelper';
import { getUserFriendlyValueFromUser } from '../utils/forms/formFieldsHelper.values';
import {
    EditOrganizationFieldsContainer,
    ShowOrganizationFieldsContainer
} from './Pages/TicketPages/TicketPagesContentLayout/TicketDetailContentLayout/TicketInfoLeftSideTabs/OrganizationFieldsContainer';

export const getCustomFields = (organizationInfo: Organization) => ({
    ...Object.fromEntries(
        (organizationInfo?.fields ?? []).map(({ key, serializedValue, userFriendlyValue, type, value }) => {
            if (!key) return null;

            const hookFormFriendlyFieldKey = key.replace(new RegExp('\\.', 'gi'), '$');

            if (!serializedValue) return [hookFormFriendlyFieldKey, null as string | number];
            if (
                [customFieldTypeMap.USER, customFieldTypeMap.UNTRUSTED_END_USER].includes(
                    type as 'USER' | 'UNTRUSTED_END_USER'
                )
            ) {
                if (!userFriendlyValue && value) {
                    // console.debug(`getCustomFields , userFriendlyValue does not exist for field with fieldKey:`, key);
                    // console.debug(`will cause userFriendlyValue to be shown on UI as:`, serializedValue);
                    return [
                        hookFormFriendlyFieldKey,
                        `${serializedValue}|${
                            getUserFriendlyValueFromUser(value as DetailedUser) ?? (value as DetailedUser)?.phone
                        }`
                    ];
                }
                return [hookFormFriendlyFieldKey, `${serializedValue}|${userFriendlyValue}`];
            }

            if (type === customFieldTypeMap.MULTI_USER) {
                if (!userFriendlyValue) {
                    // console.debug(`getCustomFields , userFriendlyValue does not exist for field with fieldKey:`, key);
                    // console.debug(`will cause userFriendlyValue to be shown on UI as:`, serializedValue);
                    return [hookFormFriendlyFieldKey, `${serializedValue}|${serializedValue}`];
                }
                const userFriendlyValueList = userFriendlyValue.split(', ');
                const hookFormFriendlyFieldValue = serializedValue
                    .split(',')
                    .map((sv, i) => `${sv}|${userFriendlyValueList[i]}`)
                    .toString();
                return [hookFormFriendlyFieldKey, hookFormFriendlyFieldValue];
            }

            return [hookFormFriendlyFieldKey, serializedValue ?? null];
        })
    )
});

const OrganizationDetailContainer = ({ organizationId }: { organizationId: string }) => {
    const { role: currentUserRole } = useCurrentUserStore();
    const isOrganizationEditable = isAdminOrAgent(currentUserRole);
    const { data: organizationData, isLoading } = useOrganizationInfo(organizationId);

    const defaultFormValues = useMemo(
        () => ({
            orgId: organizationData?.id ?? +organizationId,
            name: organizationData?.name ?? null,
            details: organizationData?.details ?? null,
            notes: organizationData?.notes ?? null,
            groupId: organizationData?.group?.id ?? null,
            [ORGANIZATION_FORM_FIELDS_KEY]: getCustomFields(organizationData)
        }),
        [organizationData, organizationId]
    );

    if (isLoading || !currentUserRole || !organizationId || !organizationData?.id)
        return (
            <Space size="small" direction="vertical" style={{ padding: '20px 25px', width: '100%' }}>
                <Space direction="vertical" className="full-width" size="large">
                    <Skeleton.Input active className="full-width" />
                    <Skeleton.Input active className="full-width" />
                    <Skeleton.Input active className="full-width" />
                    <Skeleton.Input active className="full-width" />
                </Space>
            </Space>
        );

    return (
        <EditOrganizationForm key={organizationId} defaultValues={defaultFormValues}>
            {isOrganizationEditable ? (
                <EditOrganizationFieldsContainer />
            ) : (
                <ShowOrganizationFieldsContainer organizationData={organizationData} />
            )}
        </EditOrganizationForm>
    );
};
export default observer(OrganizationDetailContainer);
